/**
 * Behavior Profile Capabilities Types
 *
 * Type definitions for behavior profile capabilities that are resolved on the backend
 * and passed to the frontend via dashboard settings. Provides type-safe access to
 * capability values through the dashboard-settings-store.
 */

import { REFUND_BY, RESOLUTION_TYPE } from "../pages/resolutions/constants";
import { REFUND_LEVELS } from "./User";

/**
 * Profile capability identifiers matching backend ProfileCapability enum
 */
export enum PROFILE_CAPABILITIES {
    POLICY_RULE_FEES = 'policy-rule-fees',
    THEME = 'theme',
    SUPPORT = 'support',
    MANUAL_EXCHANGE = 'manual-exchange',
    PRODUCT_EXCHANGE = 'product-exchange',
    REFUNDS = 'refunds',
    CANCEL_ORDER = 'cancel-order',
    BILLING_HISTORY = 'billing-history',
    SUBSCRIPTION_PLANS = 'subscription-plans',
    MANAGE_ADDONS = 'manage-addons',
    RETURN_METHOD_SHIP_WITH_ANY_CARRIER = 'return-method-ship-with-any-carrier',
    REFUND_RESOLUTION = 'refund-resolution',
    EXCHANGE_RESOLUTION = 'exchange-resolution',
    RETURN_METHOD_RETURN_TO_DROP_OFF_LOCATION = 'return-method-return-to-drop-off-location',
    RETURN_METHOD_ADDRESS_SLIP_SETTINGS = 'return-method-address-slip-settings',
    RETURN_METHOD_NO_SHIPMENT_REQUIRED = 'return-method-no-shipment-required',
    RETURN_METHOD_PACKING_SLIP_SETTINGS = 'return-method-packing-slip-settings',
    RETURN_METHOD_SHIP_WITH_PREPAID_LABEL = 'return-method-ship-with-prepaid-label',
    COMPARE_AT_PRICE = 'compare-at-price',
    EMAILS = 'emails',
    RMA_FILTERS = 'rma-filters',
    INSTANT_EXCHANGE = 'instant-exchange',
    PORTAL_CHECKOUT = 'portal-checkout',
    RETURN_LABEL_FEE_PAYMENT = 'return-label-fee-payment',
    INTERNAL_RMA_CREATION = 'internal-rma-creation',
    FEE_TAXES_CALCULATION = 'fee-taxes-calculation',
    SHOPIFY_INTEGRATION = 'shopify-integration',
    INSTANT_CREDIT_RESOLUTION = 'instant-credit-resolution',
    RETURN_POLICY_RULE = 'return-policy-rule',
    VARIANT_EXCHANGE = 'variant-exchange',
    SWITCH_TO_EXCHANGE = 'switch-to-exchange',
    WALLET = 'wallet',
    WRITE_RMA_EVENTS_IN_ORDER_NOTES = 'write-rma-events-in-order-notes',
    CUSTOM_INVENTORY = 'custom-inventory',
    STOREFRONT_SETTINGS = 'storefront-settings',
    ANALYTICS_TABS = 'analytics-tabs',
	SHIPPING_INTEGRATIONS = 'shipping-integrations',
    SBR = 'sbr',
};

/**
 * Type mapping for type-safe capability resolution
 * Maps capability IDs to their return types
 */
export type CapabilityTypeMap = {
    [PROFILE_CAPABILITIES.POLICY_RULE_FEES]: IPolicyRuleFeesCapability;
    [PROFILE_CAPABILITIES.THEME]: IThemeCapability;
    [PROFILE_CAPABILITIES.SUPPORT]: ISupportCapability;
    [PROFILE_CAPABILITIES.MANUAL_EXCHANGE]: IManualExchangeCapability;
    [PROFILE_CAPABILITIES.PRODUCT_EXCHANGE]: IProductExchangeCapability;
    [PROFILE_CAPABILITIES.MANAGE_ADDONS]: IManageAddonsCapability;
    [PROFILE_CAPABILITIES.SUBSCRIPTION_PLANS]: ISubscriptionPlansCapability;
    [PROFILE_CAPABILITIES.BILLING_HISTORY]: IBillingHistoryCapability;
    [PROFILE_CAPABILITIES.REFUNDS]: IRefundsCapability;
    [PROFILE_CAPABILITIES.CANCEL_ORDER]: ICancelOrderCapability;
    [PROFILE_CAPABILITIES.RETURN_METHOD_ADDRESS_SLIP_SETTINGS]: IReturnMethodAddressSlipSettingsCapability;
    [PROFILE_CAPABILITIES.RETURN_METHOD_NO_SHIPMENT_REQUIRED]: IReturnMethodNoShipmentRequiredCapability;
    [PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_ANY_CARRIER]: IReturnMethodShipWithAnyCarrierCapability;
    [PROFILE_CAPABILITIES.REFUND_RESOLUTION]: IRefundResolutionCapability;
    [PROFILE_CAPABILITIES.EXCHANGE_RESOLUTION]: IExchangeResolutionCapability;
    [PROFILE_CAPABILITIES.RETURN_METHOD_RETURN_TO_DROP_OFF_LOCATION]: IReturnMethodReturnToDropOffLocationCapability;
    [PROFILE_CAPABILITIES.RETURN_METHOD_PACKING_SLIP_SETTINGS]: IReturnMethodPackingSlipSettingsCapability;
    [PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_PREPAID_LABEL]: IReturnMethodShipWithPrepaidLabelCapability;
    [PROFILE_CAPABILITIES.COMPARE_AT_PRICE]: ICompareAtPriceCapability;
    [PROFILE_CAPABILITIES.EMAILS]: IEmailsCapability;
    [PROFILE_CAPABILITIES.RMA_FILTERS]: IRmaFiltersCapability;
    [PROFILE_CAPABILITIES.INSTANT_EXCHANGE]: IInstantExchangeCapability;
    [PROFILE_CAPABILITIES.PORTAL_CHECKOUT]: IPortalCheckoutCapability;
    [PROFILE_CAPABILITIES.RETURN_LABEL_FEE_PAYMENT]: IReturnLabelFeePaymentCapability;
    [PROFILE_CAPABILITIES.INTERNAL_RMA_CREATION]: IInternalRmaCreationCapability;
    [PROFILE_CAPABILITIES.FEE_TAXES_CALCULATION]: IFeeTaxesCalculationCapability;
    [PROFILE_CAPABILITIES.SHOPIFY_INTEGRATION]: IShopifyIntegrationCapability;
    [PROFILE_CAPABILITIES.INSTANT_CREDIT_RESOLUTION]: IInstantCreditResolutionCapability;
    [PROFILE_CAPABILITIES.RETURN_POLICY_RULE]: IReturnPolicyRuleCapability;
    [PROFILE_CAPABILITIES.VARIANT_EXCHANGE]: IVariantExchangeCapability;
    [PROFILE_CAPABILITIES.SWITCH_TO_EXCHANGE]: ISwitchToExchangeCapability;
    [PROFILE_CAPABILITIES.WALLET]: IWalletCapability;
    [PROFILE_CAPABILITIES.WRITE_RMA_EVENTS_IN_ORDER_NOTES]: IWriteRmaEventsInOrderNotesCapability;
    [PROFILE_CAPABILITIES.CUSTOM_INVENTORY]: ICustomInventoryCapability;
    [PROFILE_CAPABILITIES.STOREFRONT_SETTINGS]: IStorefrontSettingsCapability;
    [PROFILE_CAPABILITIES.ANALYTICS_TABS]: IAnalyticsTabsCapability;
	[PROFILE_CAPABILITIES.SHIPPING_INTEGRATIONS]: IShippingIntegrationsCapability;
	[PROFILE_CAPABILITIES.SBR]: ISBRCapability;
};

/**
 * Policy Rule Fees capability interface matching backend PolicyRuleFeesCapability DTO.
 * isSupported is true when at least one fee type is supported.
 */
export type IPolicyRuleFeesCapability = {
    isSupported: boolean;
    isRestockingFeeSupported: boolean;
    isReturnFeeSupported: boolean;
    isReturnLabelFeeSupported: boolean;
    isCancellationFeeSupported: boolean;
};

/**
 * Theme capability enum matching backend ThemeCapability
 */
export enum IThemeCapability {
    DEFAULT = 'default',
    GLOBAL_E = 'global-e',
};

/**
 * Support capability interface matching backend SupportCapability DTO
 */
export type ISupportCapability = {
    chatMethod: 'chat' | 'redirect';
    chatMethodUrl: string | null;
    showHelpSection: boolean;
    allowOnboardingCallBooking: boolean;
};

export type IManualExchangeCapability = {
    isSupported: boolean;
    isExchangeTypeSupported: boolean;
    isInternalNameSupported: boolean;
    isDisplayNameSupported: boolean;
    isDisplayDescriptionSupported: boolean;
};

/**
 * Product Exchange capability interface matching backend ProductExchangeCapability DTO
 * Includes isFeatureEnabled from ResolvesFeatureCapability trait
 */
export interface IProductExchangeCapability {
    isSupported: boolean;
    isShopNowSupported: boolean;
    isProductSearchSupported: boolean;
    isSearchByCollectionSupported: boolean;
    isFeatureEnabled: boolean;

    /** Details card fields */
    isExchangeTypeSupported: boolean;
    isInternalNameSupported: boolean;
    isDisplayNameSupported: boolean;
    isDisplayDescriptionSupported: boolean;

    /** Stock card field */
    isAllowOutOfStockSupported: boolean;
};

/**
 * Billing History capability interface matching backend BillingHistoryCapability DTO
 */
export type IBillingHistoryCapability = {
    isBillingHistorySupported: boolean;
};

/**
 * Cancel Order capability interface matching backend CancelOrderCapability DTO
 * Linked to feature CANCEL_ORDER (id 3). Unsupported for Global-E company only.
 */
export interface ICancelOrderCapability {
    isSupported: boolean;
    isFeatureEnabled: boolean;
};

/**
 * Refunds capability interface matching backend RefundsCapability DTO
 * Controls which refund-related UI elements are available based on platform/company
 * supportedRefundLevels: only "LineItem" and/or "Order" are allowed
 */
export interface IRefundsCapability {
    isAddShippingCostRefundAutomaticallySupported: boolean;
    isAddShippingCostWhenReturningAllItemsSupported: boolean;
    isConfigureWhenShippingCostIsIncludedInRefundsSupported: boolean;
    isLineItemsRefundTransactionsSupported: boolean;
    isGiftCardRefundLimitSupported: boolean;
    isStoreCreditsRefundLimitSupported: boolean;
    supportedRefundLevels: REFUND_LEVELS[];
    blockRefundWhenPending: boolean;
};

/**
 * Subscription Plans capability interface matching backend SubscriptionPlansCapability DTO
 */
export type ISubscriptionPlansCapability = {
    isSupported: boolean;
};

/**
 * Manage Add-ons capability interface matching backend ManageAddonsCapability DTO
 */
export type IManageAddonsCapability = {
    isSupported: boolean;
};

/**
 * Return Method Address Slip Settings capability interface matching backend ReturnMethodAddressSlipSettingsCapability DTO
 */
export type IReturnMethodAddressSlipSettingsCapability = {
    isSupported: boolean;
    isAddressSlipForRmaNumberSupported: boolean;
    isOrderIdSupported: boolean;
    isOrderIdBarcodeSupported: boolean;
    isWarehouseRmaIdSupported: boolean;
    isWarehouseRmaBarcodeSupported: boolean;
    isShipToSupported: boolean;
    isSendBySupported: boolean;
    isAddressSlipInstructionsSupported: boolean;
};

/**
 * Return Method Ship With Any Carrier capability interface matching backend ReturnMethodShipWithAnyCarrierCapability DTO
 */
export type IReturnMethodShipWithAnyCarrierCapability = {
    isSupported: boolean;
    isReturnMethodListSupported: boolean;
    isAddReturnMethodSupported: boolean;
    isInternalNameSupported: boolean;
    isDisplayNameSupported: boolean;
    isDisplayDescriptionSupported: boolean;
    isIncludePackingSlipSupported: boolean;
    isIncludeAddressSlipSupported: boolean;
    isDefaultRestockingLocationSupported: boolean;
    isContentSupported: boolean;
    isSendReturnShipmentNotificationsSupported: boolean;
};

/**
 * Return Method Return To Drop Off Location capability interface matching backend ReturnMethodReturnToDropOffLocationCapability DTO
 */
export type IReturnMethodReturnToDropOffLocationCapability = {
    isSupported: boolean;
    isReturnMethodListSupported: boolean;
    isAddReturnMethodSupported: boolean;
    isInternalNameSupported: boolean;
    isDisplayNameSupported: boolean;
    isDisplayDescriptionSupported: boolean;
    isDropOffLocationSourceSupported: boolean;
    isDropOffLocatorURLSupported: boolean;
    isIncludePackingSlipSupported: boolean;
    isReturnAddressSupported: boolean;
    isDefaultRestockingLocationSupported: boolean;
    isContentSupported: boolean;
};

/**
 * Return Method No Shipment Required capability interface
 * matching backend ReturnMethodNoShipmentRequiredCapability DTO
 */
export type IReturnMethodNoShipmentRequiredCapability = {
    isSupported: boolean;
    isInternalNameSupported: boolean;
    isDisplayNameSupported: boolean;
    isDisplayDescriptionSupported: boolean;
    isShippingInstructionsSupported: boolean;
};

/**
 * Return Method Packing Slip Settings capability interface
 * matching backend ReturnMethodPackingSlipSettingsCapability DTO
 */
export type IReturnMethodPackingSlipSettingsCapability = {
    isSupported: boolean;
    isPackingSlipForRmaNumberSupported: boolean;
    isOrderIdSupported: boolean;
    isOrderIdBarcodeSupported: boolean;
    isWarehouseRmaIdSupported: boolean;
    isWarehouseRmaBarcodeSupported: boolean;
    isDisplayShipToAddressSupported: boolean;
    isShipToSupported: boolean;
    isSentBySupported: boolean;
    isProductImageSupported: boolean;
    isProductNameSupported: boolean;
    isVariantSupported: boolean;
    isSkuSupported: boolean;
    isPriceSupported: boolean;
    isReturnReasonSupported: boolean;
    isPackingSlipInstructionsSupported: boolean;
};

/**
 * Return Method Ship With Prepaid Label capability interface matching backend ReturnMethodShipWithPrepaidLabelCapability DTO
 */
export type IReturnMethodShipWithPrepaidLabelCapability = {
    isSupported: boolean;
    isIncludePackingSlipSupported: boolean;
    isDefaultCustomerCompanySupported: boolean;
    isDefaultRestockingLocationSupported: boolean;
    supportedShippingConnections: string[];
    isInternalNameSupported: boolean;
    isDisplayNameSupported: boolean;
    isShippingInstructionsSupported: boolean;
    isDisplayDescriptionSupported: boolean;
};

/*
 * Refund Resolution capability interface matching backend RefundResolutionCapability DTO.
 * supportedRefundTypes: Resolution type IDs (e.g. RESOLUTION_TYPE.Credits, Refund, BankRefund, GiftCard).
 */
export type IRefundResolutionCapability = {
    isRefundAmountSettingSupported: boolean;
    isAutomaticPaymentGatewayDetectionSupported: boolean;
    supportedRefundTypes: Array<
        RESOLUTION_TYPE.Refund |
        RESOLUTION_TYPE.Credits |
        RESOLUTION_TYPE.GiftCard |
        RESOLUTION_TYPE.BankRefund
    >;
};

/**
 * Exchange Resolution capability interface matching backend ExchangeResolutionCapability DTO.
 * supportedRefundTypes: MerchantResolutionSetting refund-by values (ORIGINAL_PAYMENT_METHOD, STORE_CREDIT, NEW_GIFT_CARD).
 */
export type IExchangeResolutionCapability = {
    supportedRefundTypes: Array<REFUND_BY>;
    isExchangeOrderShippingSettingSupported: boolean;
};

/**
 * Fee Taxes Calculation capability interface matching backend FeeTaxesCalculationCapability DTO
 */
export interface IFeeTaxesCalculationCapability {
    isSupported: boolean;
};

/**
 * Portal Checkout capability interface matching backend PortalCheckoutCapability DTO
 * Includes isFeatureEnabled from ResolvesFeatureCapability trait
 */
export interface IPortalCheckoutCapability {
    isSupported: boolean;
    supportedIntegrations: string[];
    isFeatureEnabled: boolean;
};

/**
 * Internal RMA Creation capability interface matching backend InternalRmaCreationCapability DTO
 */
export interface IInternalRmaCreationCapability {
    isEnabled: boolean;
    enableFeesConfiguration: boolean;
    enableReturnReasonsConfiguration: boolean;
    enableFollowUpQuestionsConfiguration: boolean;
    enableResolutionConfiguration: boolean;
    enableReturnMethodConfiguration: boolean;
    enableRefundAutomationConfiguration: boolean;
    enableMarkAsDoneAutomationConfiguration: boolean;
    enableCustomerNotificationsConfiguration: boolean;
};

/**
 * Return Label Fee Payment capability interface matching backend ReturnLabelFeePaymentCapability DTO
 */
export interface IReturnLabelFeePaymentCapability {
    isSupported: boolean;
    isDeductOptionSupported: boolean;
    isChargeOptionSupported: boolean;
};

/**
 * Instant Exchange capability interface matching backend InstantExchangeCapability DTO
 * Includes isFeatureEnabled from ResolvesFeatureCapability trait
 */
export interface IInstantExchangeCapability {
    isSupported: boolean;
    isFeatureEnabled: boolean;
};

/**
 * RMA Filters capability interface matching backend RmaFiltersCapability DTO
 */
export type IRmaFiltersCapability = {
    isRefundAndPaymentStatusFilterSupported: boolean;
};

/**
 * Emails capability interface matching backend EmailsCapability DTO
 */
export type IEmailsCapability = {
    isExchangeFeesDraftOrderEmailSupported: boolean;
    isChargedFeesDraftOrderEmailSupported: boolean;
    isInstantCreditsEmailSupported: boolean;
    isManualCreditsEmailSupported: boolean;
    isInstantExchangeReminderFirstEmailSupported: boolean;
    isInstantExchangeReminderSecondEmailSupported: boolean;
};


/**
 * Return Policy Rule capability interface matching backend ReturnPolicyRuleCapability DTO
 */
export type IReturnPolicyRuleCapability = {
    isCustomerAddressValidationRequirementSupported: boolean;
    isCustomerAddressPrefillSupported: boolean;
    isCustomerAddressFieldsCustomizationSupported: boolean;
};

/**
 * Compare-at Price capability interface matching backend CompareAtPriceCapability DTO
 * Includes isFeatureEnabled from ResolvesFeatureCapability trait (FeatureService::COMPARE_AT_PRICE_DISCOUNT)
 */
export interface ICompareAtPriceCapability {
    isSupported: boolean;
    isVariantExchangeSupported: boolean;
    isProductExchangeSupported: boolean;
    isFeatureEnabled: boolean;
};

/**
 * Shopify Integration capability interface matching backend ShopifyIntegrationCapability DTO.
 * enableShopifyReturnsForcedValue: null = merchant choice, true = forced on, false = forced off.
 */
export type IShopifyIntegrationCapability = {
    enableShopifyReturnsForcedValue: boolean | null;
};

/**
 * Instant Credit Resolution capability interface matching backend InstantCreditResolutionCapability DTO.
 * Includes isFeatureEnabled from ResolvesFeatureCapability trait (FeatureService::RETURN_SCORE_RESOLUTION).
 */
export interface IInstantCreditResolutionCapability {
    isSupported: boolean;
    isShipLaterResolutionSupported: boolean;
    isKeepItemResolutionSupported: boolean;
    isDonateItemResolutionSupported: boolean;
    isFeatureEnabled: boolean;
};

/**
 * Variant Exchange capability interface matching backend VariantExchangeCapability DTO
 */
export interface IVariantExchangeCapability {
    isSupported: boolean;
    isSizeOptionSettingSupported: boolean;
};

/**
 * Switch To Exchange capability interface matching backend SwitchToExchangeCapability DTO
 * Includes isFeatureEnabled from ResolvesFeatureCapability trait
 */
export interface ISwitchToExchangeCapability {
    isSupported: boolean;
    isGiftCardIncentiveSupported: boolean;
    isStoreCreditsIncentiveSupported: boolean;
    isSwitchToVariantExchangeSupported: boolean;
    isSwitchToProductExchangeSupported: boolean;
    isFeatureEnabled: boolean;
};

/**
 * Wallet capability interface matching backend WalletCapability DTO
 * Supported everywhere except for Global-e company.
 */
export type IWalletCapability = {
    isSupported: boolean;
};

/**
 * Write RMA Events in Order Notes capability interface matching backend WriteRmaEventsInOrderNotesCapability DTO
 */
export interface IWriteRmaEventsInOrderNotesCapability {
    isSupported: boolean;
};

/**
 * Custom Inventory capability interface matching backend CustomInventoryCapability DTO
 * Supported only on Shopify platform for custom inventory rules
 */
export interface ICustomInventoryCapability {
    isSupported: boolean;
};

/**
 * Storefront Settings capability interface matching backend StorefrontSettingsCapability DTO
 * Controls access to storefront settings page. Supported on all platforms and companies except Global-E.
 */
export interface IStorefrontSettingsCapability {
	isSupported: boolean;
}

/**
 * Analytics Tabs capability interface matching backend AnalyticsTabsCapability DTO
 * Controls visibility of Credits and Orders tabs in Profiles Analytics section.
 * Hidden for GLBE Int and GLBE Dom companies.
 */
export interface IAnalyticsTabsCapability {
	isCreditsSupported: boolean;
	isOrdersSupported: boolean;
};

/**
 * Shipping Integrations capability interface matching backend ShippingIntegrationsCapability DTO
 * Controls visibility of shipping integrations.
 */
export interface IShippingIntegrationsCapability {
	supportedIntegrations: string[];
}

/**
 * Ship By ReturnGo (SBR) capability interface matching backend SBRCapability DTO
 * Includes isFeatureEnabled from ResolvesFeatureCapability trait (FeatureService::SHIP_BY_RETURNGO)
 * Not supported by Global-E company only.
 */
export interface ISBRCapability {
	isSupported: boolean;
	isFeatureEnabled: boolean;
};
